import { sha256 as jsSha256 } from "js-sha256";
import type { HttpMethod } from "./types";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: string[],
    public readonly data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type InvokeOptions = {
  baseUrl?: string;
  skipAuth?: boolean;
};

export type PreparedRequest = {
  url: string;
  body?: string;
  headers: Record<string, string>;
  credentials?: RequestCredentialsMode;
};

export type RequestSpec = {
  method: HttpMethod;
  path: string;
};

let globalBaseUrl = "";
export const setBaseUrl = (url: string): void => {
  globalBaseUrl = url;
};

export type RequestCredentialsMode = "omit" | "same-origin" | "include";

let globalCredentials: RequestCredentialsMode | undefined;
export const setRequestCredentials = (mode: RequestCredentialsMode): void => {
  globalCredentials = mode;
};

export type TokenProvider = {
  get: () => Promise<string | undefined>;
  refresh?: () => Promise<string | undefined>;
  header?: string;
  format?: (token: string) => string;
};

const DEFAULT_TOKEN_HEADER = "x-access-token";

let tokenProvider: TokenProvider = { get: () => Promise.resolve(undefined) };
let tokenProviderSet = false;
export const setTokenProvider = (provider: TokenProvider): void => {
  tokenProvider = provider;
  tokenProviderSet = true;
};

export const hasTokenProvider = (): boolean => tokenProviderSet;

const UNAUTHORIZED = 401;

export const isUnauthorized = (error: unknown): boolean => error instanceof ApiError && error.status === UNAUTHORIZED;

export const renewAuthorization = async (options?: InvokeOptions): Promise<boolean> => {
  if (options?.skipAuth || !tokenProvider.refresh) {
    return false;
  }
  return (await tokenProvider.refresh()) !== undefined;
};

export type RefreshTokenTransport = "body" | "cookie";

// The value of REFRESH_TOKEN_TRANSPORT_HEADER (auth.ts), repeated here to keep this module free of contract imports.
const TRANSPORT_HEADER = "x-refresh-token-transport";

let refreshTokenTransport: RefreshTokenTransport = "body";

/**
 * `cookie` (a browser: `initSpaApp` and `hydrateWww` set it) asks every sign-in for its refresh token in an HttpOnly cookie
 * instead of the body; `body` (the default, and what a device uses) keeps it in the response. A server without the refresh
 * cookie configured ignores the header and answers in the body either way.
 */
export const setRefreshTokenTransport = (transport: RefreshTokenTransport): void => {
  refreshTokenTransport = transport;
};

// Only the framework's auth endpoints (sign-in, refresh, sign-out) read it, so no other request pays a CORS preflight for it.
const AUTH_PATH_PREFIX = "/api/auth/";

const transportHeaders = (path: string): Record<string, string> =>
  refreshTokenTransport === "cookie" && path.startsWith(AUTH_PATH_PREFIX) ? { [TRANSPORT_HEADER]: "cookie" } : {};

/** Merged into every request's headers. Used by the mobile client to attach a dev-only header — see `setExtraHeaders`. */
let extraHeaders: Record<string, string> = {};
export const setExtraHeaders = (headers: Record<string, string>): void => {
  extraHeaders = headers;
};

const sha256 = async (message: string): Promise<string> => {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(message);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  return jsSha256(message);
};

const pathParam = /\/:([^/?]+)(\?)?/g;

const applyPathParams = (path: string, request: Record<string, unknown>): { path: string; rest: Record<string, unknown> } => {
  const rest = { ...request };
  const substituted = path.replace(pathParam, (segment, name: string, optional: string | undefined) => {
    const value = rest[name];
    if (value === undefined || value === null) {
      if (!optional) {
        return segment;
      }
      delete rest[name];
      return "";
    }
    delete rest[name];
    return `/${String(value)}`;
  });
  return { path: substituted, rest };
};

const queryString = (params: Record<string, unknown>): string => {
  const entries = Object.entries(params).filter(([, val]) => val !== undefined);
  if (entries.length === 0) {
    return "";
  }
  return `?${new URLSearchParams(entries.map(([k, val]) => [k, String(val)])).toString()}`;
};

const accessTokenHeader = async (): Promise<Record<string, string>> => {
  const accessToken = await tokenProvider.get();
  if (!accessToken) {
    return {};
  }
  return { [tokenProvider.header ?? DEFAULT_TOKEN_HEADER]: tokenProvider.format ? tokenProvider.format(accessToken) : accessToken };
};

export const prepareRequest = async (spec: RequestSpec, request?: Record<string, unknown>, options?: InvokeOptions): Promise<PreparedRequest> => {
  const { path, rest } = applyPathParams(spec.path, { ...request });
  const hasBody = spec.method !== "GET" && spec.method !== "DELETE";
  const url = (options?.baseUrl ?? globalBaseUrl) + path + (hasBody ? "" : queryString(rest));
  const body = hasBody ? JSON.stringify(rest) : undefined;

  const headers: Record<string, string> = body === undefined ? {} : { "Content-Type": "application/json", "x-amz-content-sha256": await sha256(body) };

  if (!options?.skipAuth) {
    Object.assign(headers, await accessTokenHeader());
  }

  const prepared: PreparedRequest = { url, body, headers: { ...headers, ...transportHeaders(spec.path), ...extraHeaders } };
  if (globalCredentials !== undefined) {
    prepared.credentials = globalCredentials;
  }
  return prepared;
};

export const sendRequest = async <T>(method: HttpMethod, prepared: PreparedRequest): Promise<T> => {
  const response = await fetch(prepared.url, {
    method,
    headers: Object.keys(prepared.headers).length > 0 ? prepared.headers : undefined,
    body: prepared.body,
    ...(prepared.credentials === undefined ? {} : { credentials: prepared.credentials }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new ApiError(response.status ?? 500, err.error?.message ?? err.message ?? "Request failed", err.details, err);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json();
};
